export type MaterialEstimateItem = {
  purchase_package_quantity?: number | null;
  purchase_package_unit?: "L" | "mL" | "kg" | "g" | "unite" | null;
  purchase_package_price_ex_vat?: number | null;
  yield_material_quantity?: number | null;
  yield_material_unit?: "L" | "mL" | "kg" | "g" | "unite" | null;
  yield_output_quantity?: number | null;
  yield_output_unit?: "m2" | "m3" | "metre_lineaire" | "unite" | null;
  default_application_passes?: number;
  loss_rate_percent?: number;
  target_margin_rate?: number | null;
  current_quantity?: number;
};

export type MaterialEstimatePreview = {
  theoreticalMaterialQuantity: number;
  packagesRequired: number;
  packagesToPurchase: number;
  projectedRemainder: number;
  theoreticalMaterialCost: number;
  purchaseCost: number;
  minimumMaterialSalePrice: number | null;
};

export const materialUnitLabels: Record<string, string> = {
  L: "L",
  mL: "mL",
  kg: "kg",
  g: "g",
  unite: "unité",
  m2: "m²",
  m3: "m³",
  metre_lineaire: "m linéaire",
};

const materialUnitConversions: Record<string, { family: string; factor: number }> = {
  L: { family: "volume", factor: 1 },
  mL: { family: "volume", factor: 0.001 },
  kg: { family: "mass", factor: 1 },
  g: { family: "mass", factor: 0.001 },
  unite: { family: "count", factor: 1 },
};

const round = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
};

export function calculateMaterialEstimate(
  item: MaterialEstimateItem | undefined,
  targetQuantity: number,
  targetUnit: string,
  applicationPasses?: number,
): MaterialEstimatePreview | null {
  const packageQuantity = Number(item?.purchase_package_quantity),
    packagePrice = Number(item?.purchase_package_price_ex_vat),
    yieldMaterial = Number(item?.yield_material_quantity),
    yieldOutput = Number(item?.yield_output_quantity),
    passes = Number(applicationPasses ?? item?.default_application_passes ?? 1),
    lossRate = Number(item?.loss_rate_percent || 0),
    marginRate = item?.target_margin_rate == null ? null : Number(item.target_margin_rate),
    packageUnit = materialUnitConversions[item?.purchase_package_unit || ""],
    yieldUnit = materialUnitConversions[item?.yield_material_unit || ""];
  if (
    !item ||
    !Number.isFinite(targetQuantity) ||
    targetQuantity <= 0 ||
    !packageQuantity ||
    !Number.isFinite(packagePrice) ||
    !yieldMaterial ||
    !yieldOutput ||
    !Number.isFinite(passes) ||
    passes <= 0 ||
    passes > 20 ||
    !packageUnit ||
    !yieldUnit ||
    packageUnit.family !== yieldUnit.family ||
    item.yield_output_unit !== targetUnit
  ) return null;
  const packageBaseQuantity = packageQuantity * packageUnit.factor,
    theoreticalBaseQuantity =
      (targetQuantity / yieldOutput) *
      yieldMaterial *
      yieldUnit.factor *
      passes *
      (1 + lossRate / 100),
    theoreticalMaterialQuantity = theoreticalBaseQuantity / yieldUnit.factor,
    packagesRequired = Math.ceil(theoreticalBaseQuantity / packageBaseQuantity),
    packagesToPurchase = Math.max(
      0,
      packagesRequired - Math.floor(Number(item.current_quantity || 0)),
    ),
    projectedRemainder =
      ((Math.floor(Number(item.current_quantity || 0)) + packagesToPurchase) *
        packageBaseQuantity -
        theoreticalBaseQuantity) /
      yieldUnit.factor,
    theoreticalMaterialCost =
      (theoreticalBaseQuantity / packageBaseQuantity) * packagePrice,
    purchaseCost = packagesToPurchase * packagePrice,
    minimumMaterialSalePrice =
      marginRate != null && marginRate >= 0 && marginRate < 100
        ? theoreticalMaterialCost / (1 - marginRate / 100)
        : null;
  return {
    theoreticalMaterialQuantity: round(theoreticalMaterialQuantity, 3),
    packagesRequired,
    packagesToPurchase,
    projectedRemainder: round(projectedRemainder, 3),
    theoreticalMaterialCost: round(theoreticalMaterialCost, 2),
    purchaseCost: round(purchaseCost, 2),
    minimumMaterialSalePrice:
      minimumMaterialSalePrice == null ? null : round(minimumMaterialSalePrice, 2),
  };
}
